<template>
  <div class="bg-white-1 border-beige-10 flex flex-col gap-3 rounded border border-solid p-5">
    <a name="details">
      <Transition mode="out-in">
        <RfBaseSkeleton v-if="skeleton" height="23" width="189" srLabel="Loading details" />
        <h2 v-else class="text-heading-1 text-beige-90">Details</h2>
      </Transition>
    </a>

    <div class="flex flex-col gap-x-8 gap-y-6 lg:flex-row">
      <div class="flex h-fit flex-1 flex-col overflow-clip">
        <RfTable
          class="w-full overflow-auto"
          :loading="skeleton"
          :tableData="{ totalCount: 4, loadMore: 4 }"
          hideHeader
          :rowSkeleton="RfPromptDetailsRowSkeleton"
        >
          <template v-if="!skeleton" #tbody>
            <template v-for="row in detailsRow" :key="row.name">
              <tr
                v-if="row.showIf"
                class="border-strokes h-full border-t border-solid first:border-t-0"
              >
                <td class="text-heading-2 text-black-2 h-px! w-0 min-w-40 py-2! pl-0!">
                  <div class="flex h-full w-full items-start">
                    <div class="flex h-7 items-center">{{ row.name }}</div>
                  </div>
                </td>
                <td class="py-2!">
                  <component
                    :is="row.component"
                    v-if="row.component"
                    v-bind="{ ...row.props, ...row.listeners }"
                  />
                </td>
                <td class="w-0! pt-1.5! pb-1! align-top!">
                  <RfButtonNew
                    v-if="row.event"
                    size="none"
                    type="plain"
                    :disabled="row.disableIf"
                    class="relative mt-1! h-6 w-6 before:absolute before:-inset-2"
                    @click="emit(row.event)"
                  >
                    <component :is="row.eventIcon || RfEditIcon" class="h-6! w-6!" />
                  </RfButtonNew>
                </td>
              </tr>
            </template>
          </template>
        </RfTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import RfTable from "@/blocks/RfTable/RfTable.vue";
import { computed } from "vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import RfPromptDetailsSchedule from "@/blocks/RfPrompt/RfPromptDetails/RfPromptDetailsSchedule.vue";
import RfPromptDetailsRowSkeleton from "@/blocks/RfPrompt/RfPromptDetails/RfPromptDetailsRowSkeleton.vue";
import RfExperimentDetailsEditable from "@/blocks/RfExperiments/RfExperimentDetailsEditable.vue";
import RfExperimentDetailsTraffic from "@/blocks/RfExperiments/RfExperimentDetailsTraffic.vue";

const emit = defineEmits(["editEditable", "editSchedule", "editTraffic"]);

const props = defineProps({
  variations: { type: Array, default: () => [] },
  editable: { type: Boolean, default: false },
  startDate: { type: String, default: "" },
  endDate: { type: String, default: "" },
  skeleton: { type: Boolean, default: false },
  timezone: { type: String, default: null },
  createdAt: { type: String, default: "" },
  updatedAt: { type: String, default: "" },
  disableEdit: { type: [Boolean, String], default: false },
  disableTraffic: { type: [Boolean, String], default: false },
  disableSchedule: { type: [Boolean, String], default: false },
});

const detailsRow = computed(() => {
  return [
    {
      name: "Editable",
      event: "editEditable",
      component: RfExperimentDetailsEditable,
      showIf: true,
      disableIf: props.disableEdit,
      props: { editable: props.editable },
    },
    {
      name: "Traffic Distribution",
      event: "editTraffic",
      component: RfExperimentDetailsTraffic,
      showIf: true,
      disableIf: props.disableTraffic,
      props: { variations: props.variations },
    },
    {
      name: "Schedule",
      event: "editSchedule",
      component: RfPromptDetailsSchedule,
      showIf: true,
      disableIf: props.disableSchedule,
      props: {
        schedule: { startDate: props.startDate, endDate: props.endDate },
        timezone: props.timezone,
        // RE-1413: startDate/endDate are naive wall clocks re-labeled with the app timezone by
        // Experiment#as_json — naiveSchedule skips the second conversion. Don't remove.
        naiveSchedule: true,
      },
    },
    {
      name: "Created at",
      component: RfPromptDetailsSchedule,
      showIf: true,
      props: {
        schedule: { startDate: props.createdAt },
        hideEndDate: true,
        timezone: props.timezone,
      },
    },
    {
      name: "Last updated at",
      component: RfPromptDetailsSchedule,
      showIf: true,
      props: {
        schedule: { startDate: props.updatedAt },
        hideEndDate: true,
        timezone: props.timezone,
      },
    },
  ];
});
</script>
